import { observable, action, computed } from 'mobx';
import { toast } from "react-toastify"
import ICustomer, { newCustFilterInstance, ICustomerCollection } from '../types/ICustomer';
import IDocument, { IDocumentCollection, IDocumentFilter, newDocFilterInstance } from '../types/IDocument';
import IMeeting, { IMeetingUserStatus, IMeetingParticipant, ISlideType, newMeetingInstance, newPresentationInstance, newParticipantInstance, validateMeeting, isMeetingValid } from '../types/IMeeting';
import IMeetingService from '../services/spi/IMeetingService';
import FirebaseMeetingService from '../services/impl/firebase/FirebaseMeetingService';
import IDocumentService from '../services/spi/IDocumentService';
import ICustomerService from '../services/spi/ICustomerService';
import FirebaseDocumentService from '../services/impl/firebase/FirebaseDocumentService';
import FirebaseCustomerService from '../services/impl/firebase/FirebaseCustomerService';
import IPaginator, { IPaginatorQueryType, newPaginatorInstance } from '../types/Paginator';
import FirebaseTemplateService from '../services/impl/firebase/FireBaseTemplateService';

class CreateMeetingStore {
	rootStore: any;
	meetingService = {} as IMeetingService;
	documentService = {} as IDocumentService;
	customerService = {} as ICustomerService;
	templateService = {} as any;
	@observable customerList: Array<ICustomer> = [];
	@observable documentList: Array<IDocument> = [];
	@observable meeting: IMeeting = newMeetingInstance();
	@observable errors:any = {};
	@observable shareUrl: string = "";
	@observable sharing: boolean = false;

	constructor(rootStore: any) {
		this.rootStore = rootStore;
		this.meetingService = new FirebaseMeetingService();
		this.documentService = new FirebaseDocumentService();
		this.customerService = new FirebaseCustomerService();
		this.templateService = new FirebaseTemplateService();
	}

	@action loadMeeting = async (meetingId: string, authUser: any) => {
		if (!meetingId) {
			this.meeting = newMeetingInstance();//Pass authUser here
			this.setAuthUser(authUser);
		} else {
			try {
				this.meeting = await this.meetingService.getMeeting(authUser.uid, meetingId);
			} catch (e) {
				toast.error("Error Loading meeting. Please try again.")
			}
		}
	}

	@action setAuthUser = (authUser:any) => {
		this.meeting.presenter.displayName = authUser.firstName + " " + authUser.lastName;
		this.meeting.presenter.imageUrl = "";
		this.meeting.presenter.id = authUser.uid;
	}

	@action setTitle = (val: string) => {
		this.meeting.title = val;
	}

	@action setDocument = (doc: IDocument) => {
		/*const slides = doc.pages? doc.pages.map((page:IDocumentPage) => {
		  return {
			type: ISlideType.link,
			content: page.url,
			//animate: 'zoomInDown'
		}}): [];
		*/
		this.meeting.presentation = newPresentationInstance({
		  docId: doc.uid ? doc.uid : '',
		  fileName: doc.file.fileName,
		  name: doc.name,
		  docUrl: doc.file.docUrl ? doc.file.docUrl : '',
			noOfSlides: doc.file.noOfPages,
			slides: doc.file.pages ? doc.file.pages.map((page: any) => {
				return {content: page.url, type: ISlideType.link}
			}): undefined,
			currentSlide: 0
		});
	}

	@action setCustomer = (cust: ICustomer) => {
		const participant: IMeetingParticipant = newParticipantInstance({
			id: cust.uid,
			displayName: cust.account.firstName + ' ' + cust.account.lastName,
			imageUrl: cust.account.image
		});
		//TODO: multi participant?
		this.meeting.participants = [];
		this.meeting.participants.push({
			id: cust.uid || '',
			displayName: cust.account.firstName + ' ' + cust.account.lastName,
			imageUrl: cust.account.image || '',
			status: IMeetingUserStatus.Offline,
			audio: {
				audioUid: 0,
				channelName: 'xxxx',
				isMute: false,
				optedIn: false
			}
		})

	}

	@action setStartTime = (ts: number) => {
		this.meeting.startTime = ts
	}

	@action setEndTime = (ts: number) => {
		this.meeting.endTime = ts
	}

	@action loadCustomers = async () => {
		const custCollection: ICustomerCollection = await this.customerService.getCustomers(newPaginatorInstance(), newCustFilterInstance());
		this.customerList = custCollection.customers;
	}

	@action loadDocuments = async () => {
		const docCollection: IDocumentCollection = await this.documentService.getDocuments(newPaginatorInstance(), newDocFilterInstance());
		this.documentList = docCollection.documents;
	}

	@action saveMeeting = async (authUser: any) => {
		//this.customer.createdAt = firebase.serverValue.TIMESTAMP; //TODO: insert who columns
		const { uid, ...meetingSnapshot } = this.meeting;
		if(isMeetingValid(this.meeting)){
			if (uid == undefined || uid === '') {
				try {
					const meet: IMeeting = await this.meetingService.createMeeting(authUser.uid, this.meeting);
					if (meet.uid)
						this.loadMeeting(meet.uid, authUser);
					toast.success("Meeting successfully scheduled with the Customer.")
				} catch (e) {
					toast.error('Error creating meeting. Please try again.')
				}
			} else {
				try {
					await this.meetingService.updateMeeting(authUser.uid, this.meeting);
					toast.success("Meeting successfully updated with the Customer.");
				} catch (e) {
					toast.error("Error saving meeting. Please try again.")
				}
			}
		} else {
			this.errors = validateMeeting(this.meeting);
		}
	}

	@action createShareableCode = async (authUser: any) => {
		let shareCodeObj = {
			meetingId: this.meeting.uid,
			userId: authUser.uid,
			createdAt: new Date().getTime(),
			lastActivity: "",
			templateContent: "",
			type: "meeting"
		}

		try {
			this.shareUrl = await this.meetingService.createSharecode(shareCodeObj);
			this.meeting.shareUrl = `${window.location.origin}/m/${this.shareUrl}`
			await this.meetingService.updateMeeting(authUser.uid, this.meeting);
		}
		catch (e) {
			toast.error('Sharing Failed! Try later!')
		}
	}

	@action shareMeeting = async (shareVia: any, authUser: any) => {
		if (this.meeting.participants) {
			let customerDetails = await this.customerService.getCustomer(this.meeting.participants[0].id);
			let shareObject: any = {
				user: authUser,
				meeting: this.meeting,
				customer: customerDetails,
				redirectUrl: this.meeting.shareUrl
			};
			this.sharing = true;

			for (let mode of shareVia) {
				let searchCode = mode == "email" ? "me" : "ms";
				let temp = await this.templateService.getTemplateByCode(searchCode);
				shareObject[mode + "Template"] = temp.content;
				shareObject[mode] = true;
				debugger
			}

			try {
				const res = await this.meetingService.shareMeeting(shareObject);
				console.log(res);
				this.sharing = false;
				toast.success('Meeting Shared!!')
				if (res.status == 200) {
					toast.success('Meeting Shared!!')
				}
			}
			catch (e) {
				throw e
			}
		}
	}
}

export default CreateMeetingStore;
